import { useState, useCallback } from 'react';
import { buildCompleteEstimatConfig, createCustomStandardval } from '../../../services/estimatConfigService';
import { ESTIMAT_CATEGORIES } from '../../../shared/config/estimatConfig';

/**
 * Custom hook för hantering av standardval (custom estimat-poster)
 * 
 * Hanterar:
 * - Laddning av dynamisk estimat-config (inkl. custom standardval)
 * - Öppna/stänga modals för att lägga till nya standardval
 * - Spara nya standardval till backend
 * - Hantera borttagning/uppdatering av standardval
 */
const useStandardval = () => {
  // State för dynamiska kategorier (med custom standardval från backend)
  const [dynamicEstimatCategories, setDynamicEstimatCategories] = useState(ESTIMAT_CATEGORIES);
  
  // State för modals
  const [showAddStandardvalModal, setShowAddStandardvalModal] = useState(false);
  const [showManageStandardvalModal, setShowManageStandardvalModal] = useState(false);
  const [standardvalModalData, setStandardvalModalData] = useState({
    category: '',
    subcategory: null,
    categoryDisplayName: '',
    subcategoryDisplayName: null
  });

  /**
   * Laddar dynamisk estimat-config från backend (inkl. custom standardval)
   */
  const loadDynamicEstimatConfig = useCallback(async () => {
    try {
      const completeConfig = await buildCompleteEstimatConfig();
      setDynamicEstimatCategories(completeConfig);
    } catch (error) {
      console.error('Fel vid laddning av dynamisk estimat-config:', error);
      // Behåll statisk config om något går fel
    }
  }, []);

  /**
   * Öppnar modal för att lägga till nytt standardval
   * @param {string} category - Kategori-nyckel (t.ex. 'projektering', 'byggoffert')
   * @param {string|null} subcategory - Subkategori-nyckel (om applicerbart)
   */
  const openAddStandardvalModal = useCallback((category, subcategory = null) => {
    const categoryData = dynamicEstimatCategories[category];
    let categoryDisplayName = categoryData?.name || category;
    let subcategoryDisplayName = null;

    if (subcategory && categoryData?.subcategories?.[subcategory]) {
      subcategoryDisplayName = categoryData.subcategories[subcategory].name;
    }

    setStandardvalModalData({
      category,
      subcategory,
      categoryDisplayName,
      subcategoryDisplayName
    });
    setShowAddStandardvalModal(true);
  }, [dynamicEstimatCategories]);

  /**
   * Sparar nytt standardval till backend
   * @param {Object} standardvalData - Data för det nya standardvalet
   */
  const handleSaveStandardval = useCallback(async (standardvalData) => {
    try {
      await createCustomStandardval(standardvalData);

      // Ladda om dynamisk config för att inkludera det nya standardvalet
      await loadDynamicEstimatConfig();
      
      // Visa success-meddelande
      alert('✅ Standardval har lagts till och kommer nu finnas tillgängligt i alla estimat!');
    } catch (error) {
      console.error('❌ Fel vid sparande av standardval:', error);
      
      // Visa fel-meddelande
      alert('❌ Fel vid sparande av standardval: ' + (error.message || 'Okänt fel'));
      
      // Re-throw så att modalen kan hantera felet
      throw error;
    }
  }, [loadDynamicEstimatConfig]);

  /**
   * Callback som anropas när standardval har tagits bort/uppdaterats
   * Laddar om config för att reflektera ändringar
   */
  const handleStandardvalDeleted = useCallback(async () => {
    // Ladda om dynamisk config efter borttagning/uppdatering
    await loadDynamicEstimatConfig();
  }, [loadDynamicEstimatConfig]);

  return {
    // State
    dynamicEstimatCategories,
    showAddStandardvalModal,
    showManageStandardvalModal,
    standardvalModalData,
    
    // Setters (för direktmanipulering om nödvändigt)
    setShowAddStandardvalModal,
    setShowManageStandardvalModal,
    
    // Actions
    loadDynamicEstimatConfig,
    openAddStandardvalModal,
    handleSaveStandardval,
    handleStandardvalDeleted,
  };
};

export default useStandardval;
